import type { User } from '@/types/session'
import { migrateApplicationStatus } from '@/utils/backwardsCompatibility'

const STORAGE_KEY = 'user'

let currentUser: User | null = null
const listeners = new Set<() => void>()

const notify = () => {
	listeners.forEach((fn) => fn())
}

export const authCache = {
	get(): User | null {
		return currentUser
	},

	set(u: User | null): void {
		if (u) {
			// Normalize legacy application_status shapes before caching
			try {
				// migrateApplicationStatus expects DbUser-like shape; cast is safe here
				// so the UI always reads a normalized object shape from localStorage.
				const normalized = migrateApplicationStatus(u as unknown as User) as User
				currentUser = normalized
			} catch {
				// fallback to raw value if migration fails
				currentUser = u
			}
		} else {
			currentUser = null
		}
		try {
			if (currentUser) localStorage.setItem(STORAGE_KEY, JSON.stringify(currentUser))
			else localStorage.removeItem(STORAGE_KEY)
		} catch {
			// ignore storage errors (private mode, quota, etc.)
		}
		notify()
	},

	loadFromStorage(): User | null {
		try {
			const raw = localStorage.getItem(STORAGE_KEY)
			currentUser = raw ? (JSON.parse(raw) as User) : null
		} catch {
			currentUser = null
		}
		return currentUser
	},

	clear(): void {
		this.set(null)
	},

	subscribe(fn: () => void): () => void {
		listeners.add(fn)
		return () => {
			listeners.delete(fn)
		}
	},
}
